/**
 * PricingPage — Reclaim's subscription pricing landing.
 *
 * Three tiers, dark luxury Aurum-aligned styling:
 *   • Founding member · $18.99/mo · "Founding member · limited"
 *   • Annual         · $159.99/yr · "Most popular · best value"  (highlighted)
 *   • Monthly        · $26.99/mo · "3-day money-back guarantee"
 *
 * Free area at /free-area-coming-soon — see CTA below the cards.
 *
 * Server enforces all amounts (PLANS dict in billing.py). The frontend
 * only sends `plan_id` + `origin_url` + `use_crypto`. Stripe-hosted
 * Checkout handles the actual card / crypto collection so we never
 * touch PCI scope.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Sparkles, Bitcoin } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ACCENT = '#CBA153';
const ACCENT_SOFT = 'rgba(203, 161, 83, 0.45)';

const FEATURES = {
  monthly: [
    'Living Vision Board + cinematic immersion',
    'Private Suite — binaural beats + meditation',
    'Aurum Edge (network growth conditioning)',
    'Voice cloning · personalised affirmations',
    'My Collection — save & re-experience',
    'Cancel anytime · 3-day money-back guarantee',
  ],
  founder: [
    'Everything in Premium',
    "Founders-only price · locked in for life",
    'First access to every new feature',
    'Direct line to founder Q&As',
    'Priority support',
  ],
  annual: [
    'Everything in Premium',
    '50% off the monthly rate',
    'Annual commitment = deeper integration',
    'No renewal anxiety · single yearly charge',
    'Includes Aurum + Golf + every program',
  ],
};

const PlanCard = ({ plan, onSelect, busy, useCrypto, demoMode }) => {
  const isHighlight = plan.highlight;
  const isFounder = plan.id === 'founder';
  const features = FEATURES[plan.id] || [];
  const perDay = (plan.price / (plan.id === 'annual' ? 365 : 30)).toFixed(2);

  return (
    <div
      className="relative rounded-3xl p-7 sm:p-9 transition-all"
      data-testid={`plan-card-${plan.id}`}
      style={{
        background: isHighlight
          ? 'linear-gradient(160deg, rgba(28, 22, 12, 0.92) 0%, rgba(14, 10, 6, 0.96) 100%)'
          : 'linear-gradient(160deg, rgba(14, 22, 38, 0.82) 0%, rgba(8, 12, 22, 0.92) 100%)',
        border: `1px solid ${isHighlight ? 'rgba(217, 180, 120, 0.55)' : 'rgba(155, 175, 220, 0.14)'}`,
        boxShadow: isHighlight
          ? '0 30px 80px rgba(120, 90, 50, 0.30), 0 0 80px rgba(217, 180, 120, 0.18)'
          : '0 20px 50px rgba(0, 6, 22, 0.55)',
      }}
    >
      {isHighlight && (
        <div
          className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-[10px] tracking-[0.32em] uppercase whitespace-nowrap"
          style={{
            background: 'linear-gradient(90deg, #CBA153 0%, #8E6F3D 100%)',
            color: '#0A0A0A',
            fontFamily: "'Manrope', sans-serif",
            fontWeight: 600,
            letterSpacing: '0.28em',
          }}
        >
          Most popular
        </div>
      )}
      {isFounder && (
        <p
          className="text-[10px] tracking-[0.32em] uppercase mb-4"
          style={{ color: ACCENT_SOFT, fontFamily: "'Manrope', sans-serif" }}
        >
          Founding member · limited
        </p>
      )}

      <h3
        className="text-2xl mb-2"
        style={{
          fontFamily: "'Playfair Display', serif",
          fontStyle: 'italic',
          color: 'rgba(245, 235, 215, 0.95)',
          letterSpacing: '0.04em',
        }}
      >
        {plan.label}
      </h3>

      <div className="flex items-baseline gap-1.5 mb-1">
        <span
          className="text-5xl"
          style={{ fontFamily: "'Playfair Display', serif", color: 'rgba(245, 235, 215, 0.95)', fontWeight: 300 }}
        >
          ${plan.price}
        </span>
        <span className="text-sm text-white/40" style={{ fontFamily: "'Manrope', sans-serif" }}>
          /{plan.period}
        </span>
      </div>
      <p className="text-[11px] text-white/30 mb-6" style={{ fontFamily: "'Manrope', sans-serif" }}>
        about ${perDay}/day
      </p>

      <p
        className="text-sm text-white/55 mb-6 leading-relaxed"
        style={{ fontFamily: "'Spectral', serif", fontStyle: 'italic' }}
      >
        {plan.blurb}
      </p>

      <ul className="space-y-3 mb-8">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <Check size={14} style={{ color: ACCENT, marginTop: 3, flexShrink: 0 }} />
            <span className="text-sm text-white/75 leading-snug" style={{ fontFamily: "'Manrope', sans-serif" }}>
              {f}
            </span>
          </li>
        ))}
      </ul>

      <button
        onClick={() => onSelect(plan.id)}
        disabled={busy}
        className="w-full py-3.5 rounded-full transition-all disabled:opacity-40"
        data-testid={`plan-cta-${plan.id}`}
        style={{
          background: isHighlight
            ? 'linear-gradient(90deg, #CBA153 0%, #8E6F3D 100%)'
            : 'transparent',
          color: isHighlight ? '#0A0A0A' : 'rgba(245, 235, 215, 0.92)',
          border: isHighlight ? 'none' : `1px solid ${ACCENT_SOFT}`,
          fontFamily: "'Manrope', sans-serif",
          fontWeight: 600,
          letterSpacing: '0.14em',
          fontSize: '0.85rem',
        }}
      >
        {busy ? 'Opening checkout…' : demoMode ? 'Demo · skip payment' : useCrypto ? 'Pay with crypto' : 'Continue'}
      </button>
    </div>
  );
};

const PricingPage = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);
  const [useCrypto, setUseCrypto] = useState(false);
  const [demoMode, setDemoMode] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/billing/plans`)
      .then((r) => r.json())
      .then((d) => setPlans(d.plans || []))
      .catch(() => setError('Could not load plans'));
    // Lightweight admin probe — Demo Mode is only shown for role=admin.
    const token = localStorage.getItem('reclaim_token');
    if (token) {
      fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.ok ? r.json() : null)
        .then((u) => { if (u && u.role === 'admin') setIsAdmin(true); })
        .catch(() => { /* not admin, that's fine */ });
    }
  }, []);

  // Handle session_id return from Stripe — surface to /billing/success page.
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get('session_id');
    if (sid) navigate(`/billing/success?session_id=${sid}`);
  }, [navigate]);

  const onSelect = async (planId) => {
    setError(null);
    setBusy(planId);
    try {
      const token = localStorage.getItem('reclaim_token');
      if (!token) {
        navigate(`/login?next=${encodeURIComponent('/pricing')}`);
        return;
      }
      const r = await fetch(`${API}/api/billing/${demoMode ? 'demo-checkout' : 'checkout'}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          plan_id: planId,
          origin_url: window.location.origin,
          use_crypto: useCrypto,
        }),
      });
      if (!r.ok) throw new Error(`checkout-failed:${r.status}`);
      const d = await r.json();
      if (d.checkout_url) window.location.href = d.checkout_url;
    } catch (e) {
      setError('Could not start checkout. Please try again.');
      setBusy(null);
    }
  };

  // Order the cards: Founder · Annual (highlight, centre) · Monthly.
  const sorted = ['founder', 'annual', 'monthly']
    .map((id) => plans.find((p) => p.id === id))
    .filter(Boolean);

  return (
    <div
      className="min-h-screen pb-24"
      style={{ background: 'radial-gradient(ellipse at 50% 25%, #0B1436 0%, #05091C 60%, #01030B 100%)' }}
      data-testid="pricing-page"
    >
      {/* Hero */}
      <div className="text-center pt-20 pb-12 px-6">
        <p
          className="text-[10px] tracking-[0.4em] uppercase mb-4"
          style={{ color: ACCENT_SOFT, fontFamily: "'Manrope', sans-serif" }}
        >
          Reclaim · Membership
        </p>
        <h1
          className="mb-5"
          style={{
            fontFamily: "'Playfair Display', serif",
            fontStyle: 'italic',
            color: 'rgba(245, 235, 215, 0.96)',
            fontSize: 'clamp(2rem, 5.4vw, 3.6rem)',
            letterSpacing: '0.02em',
            lineHeight: 1.1,
          }}
        >
          Become the version of you
          <br />you're already becoming.
        </h1>
        <p
          className="max-w-xl mx-auto text-white/55 text-sm sm:text-base leading-relaxed"
          style={{ fontFamily: "'Spectral', serif", fontStyle: 'italic' }}
        >
          Three ways to commit. One outcome — the calm, certain self that's
          already wired inside you.
        </p>
      </div>

      {/* Crypto toggle + admin-only Demo toggle */}
      <div className="flex flex-wrap justify-center items-center gap-3 mb-8 px-6">
        <button
          onClick={() => setUseCrypto((v) => !v)}
          data-testid="crypto-toggle"
          className="flex items-center gap-2 px-4 py-2 rounded-full text-xs transition-all"
          style={{
            background: useCrypto ? 'rgba(217, 180, 120, 0.10)' : 'rgba(255,255,255,0.03)',
            border: `1px solid ${useCrypto ? 'rgba(217, 180, 120, 0.45)' : 'rgba(255,255,255,0.08)'}`,
            color: useCrypto ? ACCENT : 'rgba(255,255,255,0.55)',
            fontFamily: "'Manrope', sans-serif",
          }}
        >
          <Bitcoin size={13} />
          {useCrypto ? 'Pay with crypto (USDC)' : 'Also pay with crypto'}
        </button>

        {isAdmin && (
          <button
            onClick={() => setDemoMode((v) => !v)}
            data-testid="demo-mode-toggle"
            title="Admin-only: walks through the post-payment unlock without charging — for live Aurum / Golf demos."
            className="flex items-center gap-2 px-4 py-2 rounded-full text-xs transition-all"
            style={{
              background: demoMode ? 'rgba(180, 120, 220, 0.12)' : 'rgba(255,255,255,0.03)',
              border: `1px solid ${demoMode ? 'rgba(200, 160, 240, 0.55)' : 'rgba(255,255,255,0.08)'}`,
              color: demoMode ? 'rgba(220, 190, 255, 0.95)' : 'rgba(255,255,255,0.50)',
              fontFamily: "'Manrope', sans-serif",
            }}
          >
            <span aria-hidden style={{ fontSize: '0.9em', lineHeight: 1 }}>🎭</span>
            {demoMode ? 'Demo mode ON · no charge' : 'Demo mode (admin)'}
          </button>
        )}
      </div>

      {/* Plan grid */}
      <div className="max-w-5xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
        {sorted.map((p) => (
          <PlanCard key={p.id} plan={p} onSelect={onSelect} busy={busy === p.id} useCrypto={useCrypto} demoMode={demoMode} />
        ))}
      </div>

      {error && (
        <p className="text-center text-rose-300/85 text-sm mt-6" data-testid="checkout-error">
          {error}
        </p>
      )}

      {/* Free area */}
      <div className="text-center mt-14 px-6">
        <p
          className="text-[10px] tracking-[0.32em] uppercase mb-3"
          style={{ color: ACCENT_SOFT, fontFamily: "'Manrope', sans-serif" }}
        >
          Or start free
        </p>
        <p
          className="max-w-md mx-auto text-white/55 text-sm leading-relaxed"
          style={{ fontFamily: "'Spectral', serif", fontStyle: 'italic' }}
        >
          Reclaim's <strong className="text-white/75">free area</strong> includes
          daily ritual, beginner immersions, and a sample of every premium feature
          so you can feel the difference before committing.
        </p>
        <button
          onClick={() => navigate('/')}
          className="mt-5 text-sm text-white/65 hover:text-white inline-flex items-center gap-1.5"
          style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: '0.12em' }}
          data-testid="explore-free-cta"
        >
          <Sparkles size={13} style={{ color: ACCENT }} />
          Explore the free area →
        </button>
      </div>

      {/* Trust line */}
      <div className="max-w-3xl mx-auto text-center mt-16 px-6">
        <p className="text-[11px] text-white/30 leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Secure payments by Stripe · Card · Apple Pay · Google Pay · USDC
          <br />
          Cancel anytime in your Account settings.
        </p>
      </div>
    </div>
  );
};

export default PricingPage;
